'use client';

import { Fragment } from 'react';
import type { ScratchBlock, ScratchCategory, ScratchCodeBlock } from '@jeraisonne/ai/visual-blocks';

// Codes couleurs officiels Scratch 3 (catégorie → hex de fond).
export const SCRATCH_COLORS: Record<ScratchCategory, string> = {
  motion: '#4C97FF',
  looks: '#9966FF',
  sound: '#CF63CF',
  events: '#FFBF00',
  control: '#FFAB19',
  sensing: '#5CB1D6',
  operators: '#59C059',
  variables: '#FF8C1A',
  my_blocks: '#FF6680',
  pen: '#0FBD8C',
};

/** Assombrit un hex `#rrggbb` de `amount` (0–1) pour la bordure. */
function darken(hex: string, amount = 0.15): string {
  const n = hex.replace('#', '');
  const r = parseInt(n.slice(0, 2), 16);
  const g = parseInt(n.slice(2, 4), 16);
  const b = parseInt(n.slice(4, 6), 16);
  const f = (v: number) => Math.max(0, Math.round(v * (1 - amount)));
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
}

/** Remplace les `{n}` du label par les inputs (rendus en pilules blanches). */
function renderLabel(label: string, inputs: (string | number)[] | undefined) {
  const parts = label.split(/(\{\d+\})/g);
  return parts.map((part, i) => {
    const m = part.match(/^\{(\d+)\}$/);
    if (m && inputs) {
      const value = inputs[Number(m[1])];
      return (
        <span
          key={i}
          className="mx-0.5 inline-flex items-center rounded-full bg-white/95 px-2 py-0.5 text-[11px] font-semibold text-slate-800 shadow-sm"
        >
          {value ?? ''}
        </span>
      );
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
}

function BlockRow({ block }: { block: ScratchBlock }) {
  const bg = SCRATCH_COLORS[block.category] ?? '#9966FF';
  const border = darken(bg);
  const isC = block.shape === 'c' || !!block.children?.length || !!block.elseChildren?.length;
  const isHat = block.shape === 'hat';

  const headerStyle = {
    backgroundColor: bg,
    borderColor: border,
  };

  if (!isC) {
    return (
      <div
        className={`inline-flex min-h-[1.75rem] items-center border px-3 py-1 text-[12px] font-medium text-white shadow-sm ${
          isHat ? 'rounded-b-md rounded-t-2xl' : 'rounded-md'
        }`}
        style={headerStyle}
      >
        {renderLabel(block.label, block.inputs)}
      </div>
    );
  }

  // Bloc en C : en-tête, corps indenté (barre latérale de la couleur), pied.
  return (
    <div className="flex flex-col">
      <div
        className="inline-flex min-h-[1.75rem] items-center self-start rounded-t-md border px-3 py-1 text-[12px] font-medium text-white shadow-sm"
        style={headerStyle}
      >
        {renderLabel(block.label, block.inputs)}
      </div>
      <div className="flex">
        <div className="w-3 shrink-0" style={{ backgroundColor: bg }} aria-hidden />
        <div className="flex flex-col gap-1 py-1 pl-1">
          <Stack blocks={block.children ?? []} />
        </div>
      </div>
      {block.elseChildren?.length ? (
        <>
          <div
            className="inline-flex min-h-[1.5rem] items-center self-start border px-3 py-0.5 text-[12px] font-medium text-white"
            style={headerStyle}
          >
            sinon
          </div>
          <div className="flex">
            <div className="w-3 shrink-0" style={{ backgroundColor: bg }} aria-hidden />
            <div className="flex flex-col gap-1 py-1 pl-1">
              <Stack blocks={block.elseChildren} />
            </div>
          </div>
        </>
      ) : null}
      <div className="h-2 w-16 rounded-b-md border-x border-b" style={headerStyle} aria-hidden />
    </div>
  );
}

function Stack({ blocks }: { blocks: ScratchBlock[] }) {
  return (
    <div className="flex flex-col items-start gap-1">
      {blocks.map((b) => (
        <BlockRow key={b.id} block={b} />
      ))}
    </div>
  );
}

export function ScratchCodeBlockRenderer({ block }: { block: ScratchCodeBlock }) {
  return (
    <div className="overflow-x-auto rounded-xl bg-slate-100 p-3">
      <div className="flex flex-col gap-5">
        {block.scripts.map((script, i) => (
          <Stack key={i} blocks={script} />
        ))}
      </div>
    </div>
  );
}
